import { FC } from 'react';
import { useQuery } from 'react-query';
import clsx from 'clsx';
import { queryKey } from 'constants/queryKey';
import PostApi from 'api/post';
import { PostPreviewVariant } from '@kontentino/kontentino-post-previews';
import { Button, Icon } from '@kontentino/ui';
import PostPreviewWithVariants from 'app/components/PostPreviewWithVariants';
import { CommentSection } from 'components/publicPreviews/post/CommentSection';
import PreviewExpired from 'components/publicPreviews/PreviewExpired';
import PublicUtils from 'components/publicPreviews/publicUtils';
import useTrackVisibility from 'utils/hooks/useTrackVisibility';
import useLocalStorage from 'utils/hooks/useLocalStorage';
import { BrowserStorage } from 'utils/browserStorage';
import PublicPostPreviewDownloadAssetsButton from 'components/publicPreviews/post/PublicPostPreviewDownloadAssets';
import { PublicPostPreviewConfig } from 'config/publicPostPreview';
import { PageNumberType } from '@kontentino/kontentino-constants/Pages';
import { faCheck } from '@fortawesome/pro-regular-svg-icons/faCheck';
import { faClose } from '@fortawesome/pro-regular-svg-icons/faClose';
import { useModalHistory } from 'context/ModalRouter';
import { modalRoutes } from 'constants/routes';
import {
  InstagramAudioDeviceNotice,
  isInstagramAudioSupported,
  usePostPreviewAudioMix,
} from 'app/modules/posts/modules/post/modules/instagramAudio';

type Props = {
  id: number;
  token: string;
  variant?: PostPreviewVariant;
  onVariantChange?: (variant: PostPreviewVariant) => void;
};

function getAccountFromOrigin(origin: string) {
  return origin.split('.')[0].replace('https://', '');
}

const PublicPostPreview: FC<Props> = ({ id, token }) => {
  const [ref, { wasEverVisible }] = useTrackVisibility();
  const [variant, setVariant] = useLocalStorage<PostPreviewVariant>(
    BrowserStorage.keys.Posts.PreviewVariant,
    'desktop',
  );
  const modalHistory = useModalHistory();
  const { data, isLoading, isError } = useQuery(
    queryKey.publicPostPreview({ id, token }),
    () => PostApi.publicPreview(id, token),
    { enabled: wasEverVisible },
  );

  const instagramAudio = data?.post?.attachments.instagramAudio ?? null;
  const hasInstagramAudio =
    isInstagramAudioSupported(data?.post?.page.type, data?.post?.subtype) &&
    !!instagramAudio;

  const { desktopRef, mobileRef } = usePostPreviewAudioMix({
    selection: instagramAudio,
    enabled: hasInstagramAudio,
    publicPreview: { postId: id, token },
  });

  const hasAssets =
    data?.post?.id &&
    data?.post?.attachments.cards.some((card) => card.medium || card.asset);

  const commentsEnabled =
    !PublicPostPreviewConfig.ACCOUNTS_WITH_DISABLED_COMMENTS.includes(
      getAccountFromOrigin(window.origin),
    );

  return (
    <div
      className="tw-overflow-hidden tw-rounded-[2rem] tw-bg-white"
      data-cy="public-post-preview"
      ref={ref}
    >
      {data && (
        <>
          <div className="tw-flex tw-flex-col tw-gap-6 tw-p-6 md:tw-gap-8 md:tw-p-8">
            <div className="tw-flex tw-w-full tw-flex-col tw-gap-4">
              {hasInstagramAudio && <InstagramAudioDeviceNotice />}
              <PostPreviewWithVariants
                content={{
                  ...data.post,
                  page: {
                    id: data.post.page.id,
                    name: data.post.page.name,
                    type: data.post.page.type as PageNumberType,
                    logo_src: data.post.page.logo?.src ?? null,
                  },
                  text: data.post.raw_text,
                }}
                variant={variant}
                videoPlayerRef={variant === 'desktop' ? desktopRef : mobileRef}
                use12hourFormat={!PublicUtils.publicMock.user.userMeridianTime}
                onVariantChange={setVariant}
                isDownloadAssetsHidden
                classNames={{ wrapper: 'tw-min-w-[200px] ' }}
                headerAction={
                  hasAssets && (
                    <PublicPostPreviewDownloadAssetsButton
                      post={data.post}
                      token={token}
                    />
                  )
                }
              />
            </div>

            <div className="tw-flex tw-w-full tw-gap-2">
              <Button
                className="tw-flex-1 !tw-border !tw-border-success-100 !tw-bg-white !tw-text-success-100 tw-transition-shadow hover:!tw-bg-white hover:tw-shadow-md"
                iconBefore={<Icon icon={faCheck} />}
                variant="success"
                onClick={() => {
                  modalHistory.push({
                    hash: modalRoutes.PUBLIC_INVITE_USER,
                    state: { actionType: 'cta' },
                  });
                }}
                data-name="public-link-workflow-buttons"
                data-cy="public-link-workflow-button_approve"
              >
                Approve
              </Button>
              <Button
                className="tw-flex-1 !tw-border !tw-border-danger-100 !tw-bg-white !tw-text-danger-100 tw-transition-shadow hover:!tw-bg-white hover:tw-shadow-md"
                iconBefore={<Icon icon={faClose} />}
                variant="danger"
                onClick={() =>
                  modalHistory.push({
                    hash: modalRoutes.PUBLIC_INVITE_USER,
                    state: { actionType: 'cta' },
                  })
                }
                data-name="public-link-workflow-buttons"
                data-cy="public-link-workflow-button_rework"
              >
                Rework
              </Button>
            </div>
          </div>

          {commentsEnabled && (
            <div className="tw-bg-primary-100 tw-px-4 tw-py-6 md:tw-px-6 md:tw-py-8">
              <CommentSection token={token} postId={data.post.id} />
            </div>
          )}
        </>
      )}
      {isError && (
        <div className="tw-px-4 tw-py-6 md:tw-px-6 md:tw-py-8">
          <PreviewExpired />
        </div>
      )}
      {isLoading && <PublicPostPreviewSkeleton />}
    </div>
  );
};

const Shimmer: FC<{ className?: string }> = ({ className }) => (
  <div
    className={clsx(
      'tw-bg-gradient-to-r tw-from-primary-10 tw-via-secondary-10 tw-to-success-10',
      'tw-bg-[length:200%_100%]',
      'tw-animate-[publicPreviewShimmer_2.5s_ease-in-out_infinite,publicPreviewHue_4s_ease-in-out_infinite]',
      className,
    )}
  />
);

const PublicPostPreviewSkeleton: FC = () => (
  <>
    <style>{`
      @keyframes publicPreviewShimmer {
        0% { background-position: 200% 0; }
        100% { background-position: -200% 0; }
      }
      @keyframes publicPreviewHue {
        0%, 100% { filter: hue-rotate(0deg); }
        50% { filter: hue-rotate(45deg); }
      }
    `}</style>
    <div className="tw-flex tw-flex-col tw-gap-6 tw-px-4 tw-py-6 md:tw-gap-8 md:tw-px-6 md:tw-py-8">
      <Shimmer className="tw-h-[420px] tw-w-full tw-rounded-2xl" />
      <div className="tw-flex tw-gap-2">
        <Shimmer className="tw-h-10 tw-flex-1 tw-rounded-md" />
        <Shimmer className="tw-h-10 tw-flex-1 tw-rounded-md" />
      </div>
    </div>
    <div className="tw-border-t tw-border-grayscale-20 tw-bg-grayscale-10 tw-px-4 tw-py-6 md:tw-px-6 md:tw-py-8">
      <div className="tw-flex tw-flex-col tw-gap-4">
        <div className="tw-flex tw-items-center tw-gap-2">
          <Shimmer className="tw-h-5 tw-w-5 tw-rounded" />
          <Shimmer className="tw-w-28 tw-h-5 tw-rounded" />
        </div>
        <Shimmer className="tw-h-12 tw-w-full tw-rounded-full" />
      </div>
    </div>
  </>
);

export default PublicPostPreview;
